import { useCallback } from 'react';
import type { Locale } from '@ebu/contracts';
import { useI18n } from '../../i18n/i18n';

/**
 * Read-aloud for children who cannot read yet (ADR 0017): the browser's own speech synthesis in the
 * interface language. Nothing is recorded or sent anywhere. Where speech is not available the
 * buttons simply do nothing audible (the text is still on screen).
 */

const VOICE_LANG: Readonly<Record<Locale, string>> = {
  en: 'en-GB',
  et: 'et-EE',
  fi: 'fi-FI',
  de: 'de-DE',
};

export const canSpeak = () => typeof window !== 'undefined' && 'speechSynthesis' in window;

export function speak(text: string, locale: Locale, slow = false): void {
  if (!canSpeak() || text.trim() === '') return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = VOICE_LANG[locale];
  utterance.rate = slow ? 0.9 : 1;
  window.speechSynthesis.speak(utterance);
}

export function stopSpeaking(): void {
  if (canSpeak()) window.speechSynthesis.cancel();
}

/** A speak function bound to the interface language (slower for little ones). */
export function useReadAloud(slow = false): (text: string) => void {
  const { locale } = useI18n();
  return useCallback((text: string) => speak(text, locale, slow), [locale, slow]);
}

/** The round 🔊 button: reads `text` aloud. */
export function ReadAloudButton({
  text,
  slow = false,
  className = 'read-aloud',
}: {
  text: string;
  slow?: boolean;
  className?: string;
}) {
  const { t } = useI18n();
  const say = useReadAloud(slow);
  return (
    <button
      type="button"
      className={className}
      aria-label={t('kids.readAloud')}
      title={t('kids.readAloud')}
      onClick={() => say(text)}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M4 9h4l5-4v14l-5-4H4z" fill="currentColor" />
        <path
          d="M16 8.5a4.5 4.5 0 0 1 0 7M18.5 6a8 8 0 0 1 0 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}
